// frontend/src/components/LibraryManager.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getLibraries, createLibrary, deleteLibrary, uploadDocumentToLibrary, getLibraryDocuments } from '../api';
import { useNotification } from '../contexts/NotificationContext';
import styles from './LibraryManager.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faBook, faArrowLeft, faSpinner, faCheckCircle, faExclamationTriangle, faFileArrowUp } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from './ConfirmationModal';

const LibraryManager = () => {
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const [libraries, setLibraries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newLibraryName, setNewLibraryName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [libraryToDelete, setLibraryToDelete] = useState(null);
  
  const [selectedLibrary, setSelectedLibrary] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const fileInputRef = useRef(null);

  const fetchLibraries = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getLibraries();
      setLibraries(data);
    } catch (error) {
      addNotification(t('errorLoadingLibraries'), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [addNotification, t]);

  useEffect(() => {
    fetchLibraries();
  }, [fetchLibraries]);

  useEffect(() => {
    if (selectedLibrary) {
      const fetchDocuments = async () => {
        setIsLoadingDocuments(true);
        try {
          const data = await getLibraryDocuments(selectedLibrary.id);
          setDocuments(data);
        } catch (error) {
          addNotification(t('errorLoadingDocuments'), 'error');
        } finally {
          setIsLoadingDocuments(false);
        }
      };
      fetchDocuments();
      
      const interval = setInterval(fetchDocuments, 5000); // Polling a cada 5 segundos
      return () => clearInterval(interval); // Limpa o intervalo ao desmontar ou mudar de biblioteca
    }
  }, [selectedLibrary, addNotification, t]);

  const handleCreateLibrary = async (e) => {
    e.preventDefault();
    if (!newLibraryName.trim()) {
      addNotification(t('libraryNameCannotBeEmpty'), 'warning');
      return;
    }
    setIsCreating(true);
    try {
      await createLibrary(newLibraryName);
      addNotification(t('libraryCreatedSuccess'), 'success');
      setNewLibraryName('');
      fetchLibraries();
    } catch (error) {
      addNotification(error.message || t('errorCreatingLibrary'), 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteClick = (library) => {
    setLibraryToDelete(library);
  };

  const confirmDelete = async () => {
    if (!libraryToDelete) return;
    try {
      await deleteLibrary(libraryToDelete.id);
      addNotification(t('libraryDeletedSuccess'), 'success');
      setLibraryToDelete(null);
      fetchLibraries();
    } catch (error) {
      addNotification(error.message || t('errorDeletingLibrary'), 'error');
      setLibraryToDelete(null);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file && selectedLibrary) {
      try {
        addNotification(t('uploadingAndProcessing'), 'info');
        await uploadDocumentToLibrary(selectedLibrary.id, file);
        // A lista de documentos será atualizada pelo polling
      } catch (error) {
        addNotification(error.message || t('errorUploadingDocument'), 'error');
      }
    }
    event.target.value = null; // Reseta o input para permitir o mesmo upload novamente
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processing':
        return <FontAwesomeIcon icon={faSpinner} spin title={t('statusProcessing')} />;
      case 'processed':
        return <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'var(--notification-success-bg)' }} title={t('statusProcessed')} />;
      case 'error':
        return <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: 'var(--notification-error-bg)' }} title={t('statusError')} />;
      default:
        return <FontAwesomeIcon icon={faSpinner} spin title={t('statusPending')} />;
    }
  };

  if (selectedLibrary) {
    return (
      <div className={styles.managerContainer}>
        <div className={styles.header}>
          <button onClick={() => setSelectedLibrary(null)} className={styles.backButton}>
            <FontAwesomeIcon icon={faArrowLeft} /> {t('backToLibraries')}
          </button>
          <h3 className={styles.title}>{selectedLibrary.name}</h3>
        </div>
        <div className={styles.documentSection}>
          <button className={styles.uploadButton} onClick={() => fileInputRef.current.click()}>
            <FontAwesomeIcon icon={faFileArrowUp} /> {t('uploadDocument')}
          </button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            accept=".pdf,.txt,.docx"
          />
          {isLoadingDocuments ? (
            <p>{t('loadingDocuments')}</p>
          ) : (
            <ul className={styles.documentList}>
              {documents.length > 0 ? documents.map(doc => (
                <li key={doc.id} className={styles.documentItem}>
                  <span className={styles.documentName}>{doc.original_filename}</span>
                  <span className={styles.documentStatus}>{getStatusIcon(doc.status)}</span>
                </li>
              )) : <p className={styles.noDocumentsMessage}>{t('noDocumentsInLibrary')}</p>}
            </ul>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.managerContainer}>
      <form onSubmit={handleCreateLibrary} className={styles.createForm}>
        <input
          type="text"
          value={newLibraryName}
          onChange={(e) => setNewLibraryName(e.target.value)}
          placeholder={t('newLibraryNamePlaceholder')}
          className={styles.createInput}
          disabled={isCreating}
        />
        <button type="submit" className={styles.createButton} disabled={isCreating}>
          <FontAwesomeIcon icon={faPlus} />
          <span>{isCreating ? t('creating') : t('create')}</span>
        </button>
      </form>

      {isLoading ? (
        <p>{t('loadingLibraries')}</p>
      ) : (
        <div className={styles.libraryGrid}>
          {libraries.length > 0 ? (
            libraries.map((lib) => (
              <div key={lib.id} className={styles.libraryCard} onClick={() => setSelectedLibrary(lib)}>
                <FontAwesomeIcon icon={faBook} className={styles.libraryIcon} />
                <span className={styles.libraryName}>{lib.name}</span>
                <button
                  className={styles.deleteButton}
                  onClick={(e) => { e.stopPropagation(); handleDeleteClick(lib); }}
                  title={t('deleteLibrary')}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            ))
          ) : (
            <p className={styles.noLibrariesMessage}>{t('noLibrariesFound')}</p>
          )}
        </div>
      )}

      <ConfirmationModal
        isOpen={!!libraryToDelete}
        onClose={() => setLibraryToDelete(null)}
        onConfirm={confirmDelete}
        title={t('deleteLibraryConfirmationTitle')}
        message={t('deleteLibraryConfirmationMessage', { name: libraryToDelete?.name })}
        confirmButtonText={t('delete')}
        cancelButtonText={t('cancel')}
      />
    </div>
  );
};

export default LibraryManager;